import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { FileText, LogOut, Monitor, Moon, Sun } from "lucide-react"

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command"
import { navigation, quickActions } from "@/config/navigation"
import { useTheme, type Theme } from "@/components/theme-provider"
import { useLogout } from "@/hooks/use-auth"
import { usePosts } from "@/hooks/use-posts"
import { useDebouncedValue } from "@/hooks/use-debounced-value"
import { StatusBadge } from "@/components/shared/status-badge"

interface CommandMenuContextValue {
  open: boolean
  setOpen: (open: boolean) => void
}

const CommandMenuContext = createContext<CommandMenuContextValue | undefined>(undefined)

export function CommandMenuProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        setOpen((value) => !value)
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [])

  const value = useMemo(() => ({ open, setOpen }), [open])

  return (
    <CommandMenuContext.Provider value={value}>
      {children}
      <CommandMenu open={open} onOpenChange={setOpen} />
    </CommandMenuContext.Provider>
  )
}

export function useCommandMenu() {
  const context = useContext(CommandMenuContext)
  if (!context) throw new Error("useCommandMenu must be used within CommandMenuProvider")
  return context
}

const THEME_OPTIONS: { value: Theme; label: string; icon: typeof Sun }[] = [
  { value: "light", label: "Light theme", icon: Sun },
  { value: "dark", label: "Dark theme", icon: Moon },
  { value: "system", label: "System theme", icon: Monitor },
]

function CommandMenu({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const navigate = useNavigate()
  const logout = useLogout()
  const { setTheme } = useTheme()
  const [search, setSearch] = useState("")
  const debouncedSearch = useDebouncedValue(search.trim(), 250)
  const searchingPosts = open && debouncedSearch.length >= 2

  const { data: postResults, isFetching } = usePosts(
    { page: 1, limit: 6, search: debouncedSearch },
    { enabled: searchingPosts },
  )
  const posts = searchingPosts ? (postResults?.posts ?? []) : []

  useEffect(() => {
    if (!open) setSearch("")
  }, [open])

  const run = useCallback(
    (action: () => void) => {
      onOpenChange(false)
      action()
    },
    [onOpenChange],
  )

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Command palette"
      description="Search posts, jump to a page, or run an action"
    >
      <CommandInput placeholder="Search posts, pages and actions…" value={search} onValueChange={setSearch} />
      <CommandList>
        <CommandEmpty>{searchingPosts && isFetching ? "Searching…" : "No results found."}</CommandEmpty>

        {posts.length > 0 && (
          <CommandGroup heading="Posts">
            {posts.map((post) => (
              <CommandItem
                key={post.id}
                // Include the query so cmdk's client-side filter keeps server matches (e.g. excerpt hits).
                value={`post-${post.id} ${post.title} ${debouncedSearch}`}
                onSelect={() => run(() => navigate(`/dashboard/posts/${post.id}/edit`))}
              >
                <FileText />
                <span className="truncate">{post.title}</span>
                <StatusBadge status={post.status} className="ml-auto" />
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {navigation.map((group) => (
          <CommandGroup key={group.label} heading={group.label}>
            {group.items.map((item) => (
              <CommandItem
                key={item.href}
                value={`${item.title} ${item.keywords?.join(" ") ?? ""}`}
                onSelect={() => run(() => navigate(item.href))}
              >
                <item.icon />
                {item.title}
              </CommandItem>
            ))}
          </CommandGroup>
        ))}

        <CommandSeparator />
        <CommandGroup heading="Actions">
          {quickActions.map((action) => (
            <CommandItem
              key={action.href}
              value={`${action.title} ${action.keywords?.join(" ") ?? ""}`}
              onSelect={() => run(() => navigate(action.href))}
            >
              <action.icon />
              {action.title}
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />
        <CommandGroup heading="Preferences">
          {THEME_OPTIONS.map((option) => (
            <CommandItem key={option.value} onSelect={() => run(() => setTheme(option.value))}>
              <option.icon />
              {option.label}
            </CommandItem>
          ))}
          <CommandItem value="sign out logout" onSelect={() => run(logout)}>
            <LogOut />
            Sign out
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  )
}
